import { FetchResult } from 'apollo-boost';
import { get } from 'lodash';

import { CampaignCriteriaKey, CampaignCriteriaSetTitles } from '../../declarations/filter/filter';
import { mapRowsToGroupsTruncatedBaseInfo } from '../../mappers/group/group';
import { castStringToEnum } from '../../service/CastHelpers';
import { GridScreenLinkBuilder } from '../../service/GridScreenLinkBuilder';
import {
    getAudienceTypeName,
    getRetargetingConditionRulesGroupType
} from '../../service/RetargetingConditionEditorHelpers/RetargetingConditionEditorHelpers';
import { campaigns } from '../../typings/common';
import {
    ClientData,
    Retargeting,
    RetargetingConditionRule,
    RetargetingGoal,
    RetargetingMapGoal,
    RetargetingStatus
} from '../../typings/domain';
import { FilterExpressionValueType, Operator } from '../../typings/filter';
import {
    campaignFlatStrategyFragment,
    DeleteRetargetingConditionsMutation,
    GdCampaignPlatform,
    GdGoalType,
    GdRetargetingConditionRuleItemReqInput,
    GdRetargetingConditionRuleTypeInput,
    RetargetingConditionsQueryForGroupEdit_client_retargetingConditions_rowset,
    RetargetingConditionsQueryForGroupEdit_client_retargetingConditions_rowset_campaigns,
    RetargetingConditionsQueryForGroupEdit_client_retargetingConditions_rowset_conditionRules,
    RetargetingConditionsQueryForGroupEdit_client_retargetingConditions_rowset_conditionRules_goals,
    RetargetingGoalsQuery_client_goals_rowset,
    RetargetingsQuery_client_goals_rowset,
    retargetingsRowFragment,
    retargetingsRowFragment_retargetingCondition_conditionRules,
    retargetingsRowFragment_retargetingCondition_conditionRules_goals
} from '../../typings/graphql-grid';
import { GridSourceParams } from '../../typings/grid';
import { RetargetingValue } from '../../typings/groupsEditState';
import {
    RetargetingConditionCampaignData,
    RetargetingConditionData,
    RetargetingConditionDataRulesGroup,
    RetargetingConditionEditorDataRule,
    RetargetingConditionEditorItemData,
    RetargetingConditionGoal
} from '../../typings/retargeting';
import { getValueFromApiFormat, mapStatsDataToStats } from '../common/common';
import { expressionToString } from '../filter/filter';

export type DeleteRetargetingConditionsResult = {
    success: boolean;
};

export function getRetargetingCampaignInfo(
    campaign: RetargetingConditionsQueryForGroupEdit_client_retargetingConditions_rowset_campaigns,
    login: string
): RetargetingConditionCampaignData {
    return {
        name: campaign.name,
        id: campaign.id,
        url: GridScreenLinkBuilder.getGridLink(
            {
                ulogin: login,
                filter: expressionToString({
                    field: CampaignCriteriaSetTitles[CampaignCriteriaKey.id],
                    operator: Operator.EQUALS,
                    value: {
                        type: FilterExpressionValueType.text,
                        value: campaign.id
                    }
                })
            },
            {
                view: campaigns
            }
        )
    };
}

export function mapRetargetingConditionFromApiFormat(
    conditionFromApi: RetargetingConditionsQueryForGroupEdit_client_retargetingConditions_rowset,
    login: string
): RetargetingConditionData {
    const {
        retargetingConditionId,
        name,
        description,
        availableForRetargeting,
        goalsInfo: { hasUnavailableGoals, goalDomains, hasGeoSegments },
        campaigns: campaignsInfo,
        conditionRules
    } = conditionFromApi;

    return {
        id: retargetingConditionId,
        name: name,
        description: getValueFromApiFormat(description),
        domains: goalDomains,
        campaigns: campaignsInfo ? campaignsInfo.map(campaign => getRetargetingCampaignInfo(campaign, login)) : [],
        hasUnavailableGoals: hasUnavailableGoals,
        isForRatesAdjustment: !availableForRetargeting,
        hasGeoSegment: hasGeoSegments,
        rulesGroups: mapRetargetingConditionRulesGroupsFromApiFormat(conditionRules)
    };
}

export function mapRetargetingGoalsFromApiFormat(
    goalsFromApi: RetargetingGoalsQuery_client_goals_rowset[]
): RetargetingConditionGoal[] {
    return goalsFromApi.map(goal => {
        return {
            id: goal.id,
            domain: goal.domain ? goal.domain : undefined,
            name: goal.name,
            type: goal.type,
            counter: goal.counterId ? String(goal.counterId) : undefined,
            audienceTypeName: getAudienceTypeName(goal.subtype, goal.uploadingSourceId)
        };
    });
}

export function mapRetargetingConditionRulesGroupsFromApiFormat(
    rulesGroups: RetargetingConditionsQueryForGroupEdit_client_retargetingConditions_rowset_conditionRules[]
): Array<RetargetingConditionDataRulesGroup> {
    return rulesGroups.map(rulesGroup => {
        return {
            type: rulesGroup.type,
            items: rulesGroup.goals.reduce(
                (
                    res: Array<RetargetingConditionEditorDataRule>,
                    goal: RetargetingConditionsQueryForGroupEdit_client_retargetingConditions_rowset_conditionRules_goals
                ) => {
                    const clientGoalType = getRetargetingConditionRulesGroupType(goal.type);

                    if (clientGoalType) {
                        res.push({
                            id: goal.id,
                            period: mapMetrikaGoalPeriod(goal),
                            type: clientGoalType,
                            isPeriodPristine: true,
                            isIdPristine: true,
                            errors: {}
                        });
                    }

                    return res;
                },
                []
            )
        };
    });
}

export const mapApiResponseDeleteRetargetingConditions = ({
    errors,
    data
}: FetchResult<DeleteRetargetingConditionsMutation>): DeleteRetargetingConditionsResult => {
    if (errors && errors.length) {
        return {
            success: false
        };
    } else {
        const validationErrors = get(data, 'deleteRetargetingConditions.validationResult.errors', []);

        return {
            success: validationErrors.length === 0
        };
    }
};

export function mapRetargetingEditorValueToGroupRetargetingValue(
    editorConditions: Array<RetargetingConditionEditorItemData>,
    currentGroupRetargetingValue: Array<RetargetingValue>
): Array<RetargetingValue> {
    const retargetingData = currentGroupRetargetingValue.reduce((res, dataItem) => {
        return {
            ...res,
            [dataItem.id]: dataItem
        };
    }, {});

    return editorConditions.reduce((res: Array<RetargetingValue>, condition: RetargetingConditionEditorItemData) => {
        if (condition.isSelected) {
            res.push(
                retargetingData[condition.id] || {
                    id: condition.id
                }
            );
        }

        return res;
    }, []);
}

export function mapRulesGroupsToApiFormat(
    rulesGroups: Array<RetargetingConditionDataRulesGroup>
): GdRetargetingConditionRuleItemReqInput[] {
    return rulesGroups.map(rulesGroup => {
        return {
            type: castStringToEnum(rulesGroup.type, GdRetargetingConditionRuleTypeInput),
            goals: rulesGroup.items.map(rule => {
                return {
                    id: rule.id || '',
                    time: rule.period || 0
                };
            })
        };
    });
}

export function mapRowsToRetargetingGridRows(
    targetingsRows: retargetingsRowFragment[],
    goals: RetargetingsQuery_client_goals_rowset[],
    clientInfo: ClientData,
    params: GridSourceParams
): Retargeting[] {
    const goalsMap = getGoalsMap(goals);

    return targetingsRows.reduce((res: Retargeting[], row) => {
        if (row.__typename === 'GdRetargeting' && row.retargetingCondition) {
            const campaignFlatStrategy = row.adGroup.campaign.strategy;

            res.push({
                id: String(row.retargetingId),
                isEditable: row.access.canEdit,
                group: mapRowsToGroupsTruncatedBaseInfo(row.adGroup, clientInfo, params),
                stats: mapStatsDataToStats(row.stats),
                name: row.retargetingCondition.name,
                description: getValueFromApiFormat(row.retargetingCondition.description),
                status: row.isSuspended ? RetargetingStatus.suspended : RetargetingStatus.active,
                priceContext: Number(row.priceContext),
                conditionRules: mapRetargetingConditionRulesForGridRowFromApiFormat(
                    row.retargetingCondition.conditionRules,
                    goalsMap
                ),
                // TODO: Принимать от бэка одним флагом
                // https://st.yandex-team.ru/DIRECT-99709
                isPriceContextEnabled: getIsPriceContextEnabled(campaignFlatStrategy)
            });
        }

        return res;
    }, []);
}

export const getIsPriceContextEnabled = (strategy: campaignFlatStrategyFragment): boolean => {
    const { platform } = strategy;
    const allowedPlatforms = [GdCampaignPlatform.CONTEXT, GdCampaignPlatform.BOTH];

    return !strategy.isAutoBudget && allowedPlatforms.includes(platform);
};

export const getGoalsMap = (apiGoals: RetargetingsQuery_client_goals_rowset[]): Record<string, RetargetingMapGoal> => {
    return apiGoals.reduce((acc: Record<string, RetargetingMapGoal>, apiGoal) => {
        const goal = {
            ...apiGoal,
            domain: getValueFromApiFormat(apiGoal.domain),
            counterId: getValueFromApiFormat(apiGoal.counterId)
        };

        acc[goal.id] = goal;

        return acc;
    }, {});
};

export const mapRetargetingConditionRulesForGridRowFromApiFormat = (
    apiConditionRules: retargetingsRowFragment_retargetingCondition_conditionRules[],
    goalsMap: Record<string, RetargetingMapGoal>
): RetargetingConditionRule[] => {
    return apiConditionRules.reduce((acc: RetargetingConditionRule[], apiConditionRule) => {
        acc.push({
            type: apiConditionRule.type,
            goals: apiConditionRule.goals.reduce((res: RetargetingGoal[], goalToSearch) => {
                const clientType = getRetargetingConditionRulesGroupType(goalToSearch.type);

                if (!clientType) {
                    return res;
                }

                const goal = goalsMap[goalToSearch.id];
                res.push({
                    ...goal,
                    time: mapMetrikaGoalPeriod(goalToSearch),
                    type: clientType,
                    isUnavailable: goal === undefined
                });

                return res;
            }, [])
        });

        return acc;
    }, []);
};

// Задача на перенос логики на бэк https://st.yandex-team.ru/DIRECT-100299
const mapMetrikaGoalPeriod = (
    goal: retargetingsRowFragment_retargetingCondition_conditionRules_goals
): number | undefined => {
    return goal.type === GdGoalType.GOAL ? getValueFromApiFormat(goal.time) : undefined;
};
